import { useMutation } from "@tanstack/react-query";
import { type IEditJointBillServiceRequest } from "@services/jointBillServices/types";
import { type IOverviewJointBillService } from "@services/overview/types";
import { queryClient } from "@/queryClient";
import {
  createJointBillService,
  deleteJointBillService,
  editJointBillService,
  getJBSPeriodPdf,
  getJBSPeriodProjectPdf,
  openJointBillServiceCurrentPeriod,
  transferPreviousExpectedPayments,
} from "./services";

export const useCreateJoinBillServiceMutation = () =>
  useMutation({
    mutationFn: createJointBillService,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["overview"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicePeriodList"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["insurance", "chubbBillInsuranceOrders"],
        }),
      ]);
    },
  });

export const useEditJoinBillServiceMutation = (
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"],
) =>
  useMutation({
    mutationFn: (data: IEditJointBillServiceRequest) =>
      editJointBillService(jointBillServiceId, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["overview"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["jointBillServicePeriodList"],
        }),
      ]);
    },
  });

export const useDeleteJointBillServiceMutation = () =>
  useMutation({
    mutationFn: deleteJointBillService,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["overview"],
      });
    },
  });

export const useGetJointBillServicePeriodPdfMutation = () =>
  useMutation({
    mutationFn: getJBSPeriodPdf,
  });

export const useGetJointBillServicePeriodProjectPdfMutation = () =>
  useMutation({
    mutationFn: getJBSPeriodProjectPdf,
  });

export const useOpenJointBillServiceCurrentPeriodMutation = () =>
  useMutation({
    mutationFn: openJointBillServiceCurrentPeriod,
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          "jointBillServicePeriodData",
          variables.jointBillServiceId,
          variables.periodId,
        ],
      });
    },
  });

export const useTransferPreviousExpectedPaymentsMutation = () =>
  useMutation({
    mutationFn: transferPreviousExpectedPayments,
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          "jointBillServicePeriodData",
          variables.jointBillServiceId,
          variables.periodId,
        ],
      });
    },
  });
